import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { FileSpreadsheet, X } from 'lucide-react';
import { useEffect, useState } from 'react';

import { FileUpload } from '@/presentation/components/file-upload/file-upload.js';
import { validateImportFile } from '@/presentation/pages/developments/utils/validate-import-file.js';
import { formatFileSize } from '@/shared/utils/format-file-size.js';

const MAX_IMPORT_FILE_SIZE = 20 * 1024 * 1024;
const FILE_UPLOAD_MAX_SIZE = Number.MAX_SAFE_INTEGER;

export interface ImportDevelopmentsDialogLabels {
    importExcelTitle: string;
    downloadTemplate: string;
    dragOrClickUpload: string;
    acceptedImportFormats: string;
    invalidImportFormat: string;
    importFileTooLarge: string;
    selectedFile: string;
    cancel: string;
    importExcel: string;
    importing: string;
    removeFile: string;
}

interface Props {
    open: boolean;
    submitting: boolean;
    error: string | null;
    selectedFile: File | null;
    onClose: () => void;
    onFileSelected: (file: File) => void;
    onClearFile: () => void;
    templateHref: string;
    templateFilename: string;
    onImport: () => void;
    labels: ImportDevelopmentsDialogLabels;
}

export function ImportDevelopmentsDialog({ open, submitting, error, selectedFile, onClose, onFileSelected, onClearFile, templateHref, templateFilename, onImport, labels }: Props) {
    const [validationError, setValidationError] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            setValidationError(null);
        }
    }, [open]);

    const handleFileSelected = (file: File) => {
        const result = validateImportFile(file, MAX_IMPORT_FILE_SIZE);
        if (result.valid) {
            setValidationError(null);
            onFileSelected(file);
            return;
        }
        setValidationError(labels[result.errorKey as keyof ImportDevelopmentsDialogLabels]);
    };

    const handleClearFile = () => {
        setValidationError(null);
        onClearFile();
    };

    const handleClose = () => {
        if (!submitting) {
            setValidationError(null);
            onClose();
        }
    };

    const canImport = selectedFile !== null && validationError === null;

    return (
        <Dialog open={open} onClose={handleClose} disableEscapeKeyDown={submitting} maxWidth="xs" fullWidth data-testid="import-developments-dialog">
            <DialogTitle>{labels.importExcelTitle}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {error && (
                        <Alert severity="error" sx={{ whiteSpace: 'pre-line', maxHeight: 220, overflowY: 'auto' }} data-testid="import-error">
                            {error}
                        </Alert>
                    )}
                    {validationError && <Alert severity="error">{validationError}</Alert>}
                    <FileUpload
                        onFileSelected={handleFileSelected}
                        accept=".xlsx"
                        maxSize={FILE_UPLOAD_MAX_SIZE}
                        uploading={submitting}
                        label={labels.dragOrClickUpload}
                        hint={labels.acceptedImportFormats}
                    />
                    {selectedFile && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            <FileSpreadsheet size={20} />
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" fontWeight={500}>
                                    {selectedFile.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {formatFileSize(selectedFile.size)}
                                </Typography>
                            </Box>
                            <IconButton
                                onClick={handleClearFile}
                                disabled={submitting}
                                size="small"
                                aria-label={labels.removeFile}
                                title={labels.removeFile}
                                data-testid="import-clear-file"
                            >
                                <X size={16} />
                            </IconButton>
                        </Box>
                    )}
                    <Button component="a" href={templateHref} download={templateFilename} variant="outlined" disabled={submitting} fullWidth data-testid="import-download-template">
                        {labels.downloadTemplate}
                    </Button>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={submitting} data-testid="import-cancel">
                    {labels.cancel}
                </Button>
                <Button onClick={onImport} variant="contained" sx={{ color: 'white' }} disabled={submitting || !canImport} data-testid="import-submit">
                    {submitting ? labels.importing : labels.importExcel}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
